<script setup lang="ts">
/**
 * The toast stack. Mounted once by `AppShell`; every notice in the app is
 * raised through `pushToast` and lands here.
 *
 * `aria-live="polite"` rather than `assertive`: these confirm an action the
 * user just took, so interrupting a screen reader mid-sentence would be rude
 * and no more informative.
 */
import { X } from '@lucide/vue'
import { useI18n } from 'vue-i18n'

import { dismissToast, toasts, type ToastTone } from '../../composables/toasts.js'

const { t } = useI18n()

const ALERT_CLASS: Record<ToastTone, string> = {
  success: 'alert-success',
  error: 'alert-error',
  info: 'alert-info',
}
</script>

<template>
  <div class="toast toast-end z-50" role="status" aria-live="polite">
    <div
      v-for="toast in toasts"
      :key="toast.id"
      class="alert max-w-sm items-start gap-3 text-sm"
      :class="ALERT_CLASS[toast.tone]"
    >
      <span class="min-w-0">{{ toast.message }}</span>
      <button
        type="button"
        class="btn btn-ghost btn-xs btn-square"
        :aria-label="t('common.toast.dismiss')"
        @click="dismissToast(toast.id)"
      >
        <X class="size-3.5" />
      </button>
    </div>
  </div>
</template>
